import { Link } from "react-router";

import { stamp } from "@/lib/format";
import { placeLabel } from "@/lib/places";
import type {
  MovePoint,
  MoveRow,
  SerialCard,
  SerialSpisanie,
} from "@/lib/providers";

import { TaskMark } from "./TaskMark";

/**
 * Таблица перемещений — ОДНА на все ленты склада.
 *
 * Лент три, и вопросы у них разные: серия спрашивает «что было с этой вещью»,
 * баркод — «что было со всеми вещами размера», место — «что здесь
 * происходило». Строка при этом у всех трёх одна и та же, потому что
 * перемещение — одна сущность: тот же час, те же оба конца, та же задача FBS и
 * тот же человек. Названное на одном экране одним набором колонок, а на
 * соседнем другим, оно читалось бы как два разных события.
 *
 * До 10.09.26 таблица стояла копией в карточке серии и в разделе «Баркод»;
 * третья копия приехала бы вместе с карточкой короба. Копии и разошлись бы —
 * на первой правке одной из них, ровно там, где две ленты начали бы сверять
 * глазами.
 *
 * `withSerial` — колонка с номером вещи. Нужна там, где строки от РАЗНЫХ
 * вещей (баркод, место): без номера такая лента читается как один непрерывный
 * маршрут, которым она не является. В карточке серии номер у всех строк один
 * и тот же, и колонка была бы столбцом одного повторяющегося числа.
 *
 * `withCard` — сам ТОВАР рядом с номером: фотография и «баркод · размер»
 * (задача владельца 10.09.26). Флаг ОТДЕЛЬНЫЙ от `withSerial`, и это не
 * дробление ради дробления: у ленты баркода строки от разных вещей, но товар у
 * них один и тот же — тот, по которому лента и отобрана, — и столбец
 * одинаковых фотографий был бы ровно тем шумом, из-за которого номер вещи не
 * показывают в ленте серии. В ленте МЕСТА товары разные, и номер серии сам по
 * себе не отвечает, платье уехало или футболка.
 */
export function MoveFeed({
  moves,
  tasksKnown,
  caption,
  withSerial = false,
  withCard = false,
}: {
  moves: MoveRow[];
  /** Спрашивали ли базу задач FBS. `false` — не «задачи нет», а «не знаем». */
  tasksKnown: boolean;
  caption: string;
  withSerial?: boolean;
  withCard?: boolean;
}) {
  return (
    <div className="overflow-auto rounded-[var(--radius-card)] border border-border bg-bg-subtle">
      <table className="w-full border-collapse text-[13px]">
        <caption className="sr-only">{caption}</caption>
        <thead className="hidden md:table-header-group">
          <tr>
            <th scope="col" className={TH + " text-left"}>
              Когда
            </th>
            {withSerial ? (
              <th scope="col" className={TH + " text-left"}>
                {withCard ? "Вещь" : "Серия"}
              </th>
            ) : null}
            <th scope="col" className={TH + " text-left"}>
              Откуда
            </th>
            <th scope="col" className={TH + " text-left"}>
              Куда
            </th>
            {/* Задача FBS стоит РЯДОМ с местами, а не в конце строки: вопрос
                «кто положил вещь в этот короб» читается вместе с самим
                коробом, а не после типа и фамилии. */}
            <th scope="col" className={TH + " text-left"}>
              Задача
            </th>
            <th scope="col" className={TH + " text-left"}>
              Тип
            </th>
            <th scope="col" className={TH + " text-left"}>
              Кто
            </th>
          </tr>
        </thead>
        <tbody className="block md:table-row-group">
          {moves.map((row) => (
            <tr
              key={row.move_id}
              data-annot={`перемещение ${row.move_id}`}
              className="mb-2 block rounded-[var(--radius-ui)] border border-border bg-bg p-2 md:mb-0 md:table-row md:rounded-none md:border-0 md:border-b md:border-border md:bg-transparent md:p-0 md:hover:bg-bg-hover"
            >
              <Cell label="Когда" align="tnum whitespace-nowrap">
                {stamp(row.at)}
              </Cell>
              {withSerial ? (
                <Cell label={withCard ? "Вещь" : "Серия"} align="tnum">
                  <Item serial={row.serial} card={withCard ? row.card : null} />
                </Cell>
              ) : null}
              <Cell label="Откуда">
                <PlaceName point={row.from} />
              </Cell>
              <Cell label="Куда">
                <PlaceName point={row.to} />
              </Cell>
              <Cell label="Задача">
                <TaskMark task={row.task} known={tasksKnown} />
              </Cell>
              <Cell label="Тип">
                <MoveKind type={row.type} spisanie={row.spisanie} />
              </Cell>
              <Cell label="Кто">
                <span className="text-fg-muted">{row.user || "—"}</span>
              </Cell>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const TH =
  "sticky top-0 z-10 whitespace-nowrap border-b border-border bg-bg-elevated " +
  "px-2.5 py-2 text-[11px] font-medium uppercase tracking-[0.04em] text-fg";

/**
 * Тип перемещения, а под ним — ПРИЧИНА, если это было списание.
 *
 * «Списано было» говорит, что с вещью сделали, но не говорит зачем (задача
 * владельца 11.09.26), а зачем — это категория списания: «fbswb» значит
 * «уехала в поставку ВБ» и разбирается автоматом, «Утеря» — совсем другой
 * разговор к тому же складу. Категория печатается ИМЕНЕМ, а не номером: «29»
 * требует держать справочник в голове — то же правило, что в карточке серии.
 *
 * Снятие списания подписано отдельно: у него тип «Ручные», как у любой ручной
 * работы склада, и одна категория без слова читалась бы как ещё одно
 * списание, а вещь этой строкой вернули в оборот.
 *
 * Комментарий и номер строки списания — в подсказке: комментарий бывает
 * длиной в предложение («FBS WB: задание …, поставка …»), а в колонке нужен
 * ответ на «почему», а не весь рассказ.
 */
function MoveKind({
  type,
  spisanie,
}: {
  type: string | null;
  spisanie: SerialSpisanie | null;
}) {
  const kind = <span className="text-fg-muted">{type || "—"}</span>;
  if (!spisanie) return kind;
  const name = spisanie.category || `Категория ${spisanie.category_id}`;
  const hint = [
    spisanie.available === 1
      ? `Снятие списания № ${spisanie.spisanie_id}`
      : `Списание № ${spisanie.spisanie_id}`,
    spisanie.comment,
  ]
    .filter(Boolean)
    .join("\n");
  return (
    <span className="flex flex-col" data-annot="списание" title={hint}>
      {kind}
      <span className="whitespace-nowrap text-[11px] text-fg">
        {spisanie.available === 1 ? `снятие · ${name}` : name}
      </span>
    </span>
  );
}

/**
 * ЧТО именно переехало: номер вещи, а рядом — сам товар.
 *
 * Номер серии отвечает на «какая вещь», но не на «какая ВЕЩЬ»: по числу
 * 9519650 не понять, платье это или футболка, а над коробом стоят с вещью в
 * руках (задача владельца 10.09.26). Поэтому рядом с номером — фотография и
 * цепочка «баркод · размер», каждое звено которой ведёт в свой раздел:
 * фото в товар, баркод в размер, номер в саму вещь.
 *
 * Товара нет (`card = null`) — остаётся один номер, и это законный ответ:
 * вещь, заведённую до маркетплейса, на него не заводили.
 */
function Item({ serial, card }: { serial: number; card: SerialCard | null }) {
  const number = (
    // Ссылкой: строка ленты — это чужая вещь, и разбор «почему она уехала»
    // продолжается в её карточке.
    <Link to={`/serial/${serial}`} className="text-fg hover:underline">
      {serial}
    </Link>
  );
  if (!card) return number;
  return (
    <span className="flex items-center gap-2">
      <Link
        to={`/products/${card.model_id}`}
        title={card.name || `Товар ${card.model_id}`}
        data-annot="фото"
        className="flex h-[38px] w-[28px] flex-none items-center justify-center overflow-hidden rounded-[var(--radius-ui)] border border-border bg-bg-subtle text-[9px] text-fg-muted"
      >
        {card.image_url ? (
          <img
            src={card.image_url}
            alt={card.name}
            referrerPolicy="no-referrer"
            loading="lazy"
            className="h-full w-full object-cover"
            onError={(e) => {
              // Битая ссылка на чужой картинке — обычное дело, и рамка с
              // номером модели честнее сломанного значка браузера.
              e.currentTarget.style.display = "none";
            }}
          />
        ) : (
          <span className="tnum">{card.model_id}</span>
        )}
      </Link>
      <span className="flex flex-col">
        {number}
        <Link
          to={`/barcode/${card.barcode}`}
          title="Где лежит и где лежал этот размер"
          className="whitespace-nowrap text-[11px] text-fg-muted hover:text-fg hover:underline"
        >
          {card.barcode}
          {card.size ? ` · ${card.size}` : ""}
        </Link>
      </span>
    </span>
  );
}

/**
 * Конец перемещения: имя места и город под ним — И ССЫЛКА НА САМО МЕСТО.
 *
 * Город печатается ОТДЕЛЬНО, а не подразумевается именем: имена мест несут
 * склад («Невиномыск Короб 10945»), номера коробов у городов сквозные, и
 * «Короб 4977» из Воронежа читался бы как ростовский.
 *
 * Ссылка ведёт в раздел «Место» (задача владельца 10.09.26): у строки ленты два
 * конца, и про каждый спрашивают «а что было ЗДЕСЬ». Ведёт она именно туда, а
 * не в карточку короба: концом перемещения бывает и стол подбора, и торговый
 * зал, и «РНД Списано», а карточка короба отбирает места каноном и на всём
 * остальном отвечает 404.
 */
function PlaceName({ point }: { point: MovePoint }) {
  if (point.place_id === null) {
    return <span className="text-fg-muted">—</span>;
  }
  return (
    <Link
      to={`/place/${point.place_id}`}
      title={point.name || `Место ${point.place_id}`}
      className="flex flex-col hover:underline"
    >
      {/* Зовётся место ОБЩИМ правилом: короб — номером, прочие — именем. Имя
          короба из CRM («Невиномыск Невиномыск Короб 10988») занимало в
          колонке три строки и повторяло город, стоящий тут же подписью; целиком
          оно осталось в подсказке. */}
      <span className="whitespace-nowrap">
        {placeLabel({
          place_id: point.place_id,
          name: point.name,
          code: point.code,
          korob: point.korob,
        })}
      </span>
      {point.city || point.group ? (
        <span className="text-[11px] text-fg-muted">
          {[point.city, point.group].filter(Boolean).join(" · ")}
        </span>
      ) : null}
    </Link>
  );
}

/**
 * Ячейка, которая на узком экране становится строкой «подпись — значение».
 * Тот же приём, что во всех таблицах кабинета.
 */
function Cell({
  label,
  align,
  children,
}: {
  label: string;
  align?: string;
  children: React.ReactNode;
}) {
  return (
    <td
      data-label={label}
      className={
        "flex items-center justify-between gap-3 border-b border-border/60 py-1 last:border-0 " +
        "before:text-[11px] before:uppercase before:tracking-[0.04em] before:text-fg-muted before:content-[attr(data-label)] " +
        "md:table-cell md:border-0 md:px-2.5 md:py-1.5 md:before:content-none " +
        (align ?? "")
      }
    >
      {children}
    </td>
  );
}
